import React, { useState, useEffect, useCallback } from 'react';
import { 
  IRadioComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormRadioProps {
  id: string;
  properties: IRadioComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormRadio: React.FC<DfFormRadioProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  // Validate field
  const validateField = useCallback((value: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && (!value || value.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string) => {
    setSelectedValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Handle radio change
  const handleRadioChange = (optionValue: string) => {
    handleValueChange(optionValue);
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes
  useEffect(() => {
    if (formValue !== selectedValue) {
      setSelectedValue(formValue);
    }
  }, [formValue, selectedValue]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get layout class for inline/vertical layout
  const getLayoutClass = (): string => {
    return properties?.basic?.inlineLayout ? 'inline-layout' : 'vertical-layout';
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <>
    <div className={`form-group radio-group ${getLabelAlignmentClass()} ${getLayoutClass()} ${className}`}>
      {!hideLabel && properties.basic.label ? (
        <>
          <label className="form-label">
            {properties.basic.label}
            {properties.validation.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
          
          <div className="input-wrapper">
            <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
              {(properties.options || []).map((option, index) => (
                <div key={index} className="form-check">
                  <input
                    type="radio"
                    id={`${id}-${index}`}
                    name={id}
                    value={option.value}
                    checked={selectedValue === option.value}
                    onChange={() => handleRadioChange(option.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={shouldDisable || option.disabled}
                    className="form-check-input"
                  />
                  <label 
                    htmlFor={`${id}-${index}`} 
                    className="form-check-label"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValue)}
        isTouched={isTouched}
        mode={mode}
      />
          </div>
        </>
      ) : (
        <div className="input-wrapper">
          <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
            {(properties.options || []).map((option, index) => (
              <div key={index} className="form-check">
                <input
                  type="radio"
                  id={`${id}-${index}`}
                  name={id}
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => handleRadioChange(option.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={shouldDisable || option.disabled}
                  className="form-check-input"
                />
                <label 
                  htmlFor={`${id}-${index}`} 
                  className="form-check-label"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValue)}
        isTouched={isTouched}
        mode={mode}
      />
        </div>
      )}

    </div>
    </>
  );
};

export default DfFormRadio;
